"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.KeyContext = void 0;
const fanout_1 = require("thingies/lib/fanout");
const Key_1 = require("./Key");
const KeySourceDoc_1 = require("./KeySourceDoc");
const KeySourceEl_1 = require("./KeySourceEl");
const KeyMap_1 = require("./KeyMap");
const KeySet_1 = require("./KeySet");
const KeySequenceMatcher_1 = require("./KeySequenceMatcher");
const printTree_1 = require("tree-dump/lib/printTree");
class KeyContext {
    /**
     * Creates a root `KeyContext` which binds to `window` and `document` key
     * events.
     *
     * @param name Provide for debugging, used in `.toString()`.
     */
    static global(name, opts) {
        const ctx = new KeyContext(void 0, name);
        const source = new KeySourceDoc_1.KeySourceDoc(opts);
        const unbind = source.bind(ctx);
        return [ctx, unbind];
    }
    constructor(parent = void 0, name = parent ? 'child' : 'root') {
        this.parent = parent;
        this.name = name;
        this.onChange = new fanout_1.FanOut();
        /** All currently pressed keys. */
        this.pressed = new KeySet_1.KeySet();
        /** History of last N pressed keys. */
        this.history = [];
        this.historyLimit = 25 /* KeyControllerConstants.HistoryLimit */;
        /** All live child contexts. */
        this.children = new Set();
        /** The child currently receiving fed events (focus target). */
        this.active = undefined;
        /** True when this context has no own source and relies on parent to feed it. */
        this._fed = true;
        /** Unbind callback for this context's own KeySource, if any. */
        this._sourceUnbind = undefined;
        /** Optional remap of raw `event.key` values to canonical key names. */
        this.remap = undefined;
        /** Timeout (ms) between consecutive sequence steps. Default: 1000. */
        this.seqTimeout = 1000;
        // ------------------------------------------------------- pausing / resuming
        this.paused = false;
        this.map = new KeyMap_1.KeyMap();
        this.seqMatcher = new KeySequenceMatcher_1.KeySequenceMatcher(this.map.sequenceMap.root, this.seqTimeout);
    }
    child(name, source) {
        const child = new KeyContext(this, name);
        this.children.add(child);
        const keySource = typeof HTMLElement !== 'undefined' && source instanceof HTMLElement
            ? new KeySourceEl_1.KeySourceEl(source)
            : source != null && typeof source === 'object' && 'bind' in source
                ? source
                : undefined;
        child._fed = !keySource;
        child._sourceUnbind = keySource?.bind(child);
        // Auto-activate first fed child so parent events reach it without explicit focus()
        if (child._fed && !this.active)
            this.active = child;
        return child;
    }
    /** Mark this context as the active child up the ancestor chain. */
    focus() {
        const parent = this.parent;
        if (parent) {
            parent.active = this;
            parent.focus();
        }
    }
    dispose() {
        // 1. Take snapshot and clear children first (prevents re-entrant removal)
        const children = [...this.children];
        this.children.clear();
        this.active = undefined;
        // 2. Recursively dispose children
        for (const child of children)
            child.dispose();
        // 3. Unbind own source
        this._sourceUnbind?.();
        this._sourceUnbind = undefined;
        // 4. Remove self from parent
        const parent = this.parent;
        if (parent) {
            parent.children.delete(this);
            if (parent.active === this)
                parent.active = undefined;
        }
    }
    bind(definitions) {
        return this.map.bind(definitions);
    }
    setChord(sig, action, options) {
        this.map.setChord(sig, action, options);
    }
    delChord(sig, action) {
        this.map.delChord(sig, action);
    }
    setRemap(from, to) {
        (this.remap ?? (this.remap = new Map())).set(from, to);
    }
    delRemap(from) {
        this.remap?.delete(from);
        if (this.remap?.size === 0)
            this.remap = undefined;
    }
    pause() {
        this.paused = true;
    }
    resume() {
        this.paused = false;
    }
    /** ------------------------------------------------------- {@link KeySink} */
    onPress(press) {
        const child = this.active;
        if (child) {
            if (child._fed) {
                let leaf = child;
                while (leaf.active && leaf.active._fed)
                    leaf = leaf.active;
                leaf.onPress_(press);
            }
        }
        else {
            this.onPress_(press);
        }
    }
    /** Propagate up on press. */
    onPress_(press) {
        if (this.paused)
            return;
        if (press.event?.isComposing || press.key === 'Dead')
            return;
        const original = press;
        const remapTo = this.remap?.get(press.key);
        if (remapTo)
            press = new Key_1.Key(remapTo, press.ts, press.mod, press.event, press.code);
        const { pressed, history } = this;
        pressed.add(press);
        history.push(press);
        while (history.length > this.historyLimit)
            history.shift();
        // Chord check runs first. If a chord is matched the key that completed
        // it does NOT fire its single-key binding (eager chord, silent single).
        const chordMatches = this.map.matchChord(pressed);
        if (chordMatches) {
            press.propagate = false;
            for (const match of chordMatches) {
                match.action(pressed);
                if (match.propagate)
                    press.propagate = true;
            }
        }
        else {
            const matches = this.map.matchPress(press);
            if (matches) {
                press.propagate = false;
                for (const match of matches) {
                    match.action(press);
                    if (match.propagate)
                        press.propagate = true;
                }
            }
        }
        // Sequence matching.
        if (!this.map.sequenceMap.isEmpty()) {
            const seqMatches = this.seqMatcher.advance(press.sig(), press.ts);
            if (seqMatches) {
                press.propagate = false;
                for (const match of seqMatches) {
                    match.action();
                    if (match.propagate)
                        press.propagate = true;
                }
            }
        }
        this.onChange.emit();
        if (press.propagate) {
            const parent = this.parent;
            if (parent) {
                original.propagate = press.propagate;
                parent.onPress_(original);
            }
        }
    }
    onRelease(release) {
        const child = this.active;
        if (child) {
            if (child._fed) {
                let leaf = child;
                while (leaf.active && leaf.active._fed)
                    leaf = leaf.active;
                leaf.onRelease_(release);
            }
        }
        else {
            this.onRelease_(release);
        }
    }
    /** Propagate up on release. */
    onRelease_(release) {
        const original = release;
        const remapTo = this.remap?.get(release.key);
        if (remapTo)
            release = new Key_1.Key(remapTo, release.ts, release.mod, release.event, release.code);
        this.pressed.remove(release);
        if (release.key === 'Meta')
            this.pressed.clearNonMods();
        if (this.paused)
            return;
        if (release.event?.isComposing || release.key === 'Dead')
            return;
        const matches = this.map.matchRelease(release);
        if (matches) {
            release.propagate = false;
            for (const match of matches) {
                match.action(release);
                if (match.propagate)
                    release.propagate = true;
            }
        }
        this.onChange.emit();
        if (release.propagate) {
            const parent = this.parent;
            if (parent) {
                original.propagate = release.propagate;
                parent.onRelease_(original);
            }
        }
    }
    onReset() {
        this.pressed.reset();
        const child = this.active;
        if (child) {
            if (child._fed)
                child.onReset();
        }
        else {
            this.onReset_();
        }
    }
    onReset_() {
        this.seqMatcher.reset();
        this.onChange.emit();
    }
    /** Called when underlying DOM element gains focus. */
    onFocus() {
        this.onReset();
        this.focus();
    }
    /** ----------------------------------------------------- {@link Printable} */
    toString(tab) {
        return ('ctx (' +
            this.name +
            (this.active ? ' → ' + this.active.name : '') +
            ')' +
            (0, printTree_1.printTree)(tab, [
                () => 'history: ' + this.history.map((k) => k.sig()).join(', '),
                (tab) => this.pressed.toString(tab),
                ...[...this.children].map((child) => (tab) => child.toString(tab)),
            ]));
    }
}
exports.KeyContext = KeyContext;
